import { useState } from "react";
import axios from "axios";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  TextField,
} from "@mui/material";
import { base_url } from "../../config";

const POLL_INTERVAL_MS = 1500;

export default function Mailing() {
  const [subject, setSubject] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [testEmails, setTestEmails] = useState("");

  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [recipientCount, setRecipientCount] = useState(null);
  const [recipientsSample, setRecipientsSample] = useState([]);
  const [error, setError] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [batch, setBatch] = useState(null); // { total, sent, failures, done }
  const [sending, setSending] = useState(false);

  const ready = subject.trim() !== "" && markdown.trim() !== "";

  const loadPreview = async () => {
    setError("");
    setPreviewing(true);
    try {
      const res = await axios.post(`${base_url}/api/admin/mailing/preview`, {
        subject,
        markdown,
        test: testEmails.trim() || undefined,
      });
      setPreviewHtml(res.data.html);
      setRecipientCount(res.data.recipient_count);
      setRecipientsSample(res.data.recipients_sample);
    } catch (e) {
      setError(
        e.response?.data?.error || "Could not build the preview. Please try again."
      );
    } finally {
      setPreviewing(false);
    }
  };

  const pollStatus = (batchId) => {
    const poll = async () => {
      try {
        const res = await axios.get(
          `${base_url}/api/admin/mailing/status/${batchId}`
        );
        setBatch(res.data);
        if (!res.data.done) {
          setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          setSending(false);
        }
      } catch (e) {
        setError("Lost track of the sending job. Check back later.");
        setSending(false);
      }
    };
    poll();
  };

  const doSend = async (test) => {
    setError("");
    setSending(true);
    setBatch(null);
    try {
      const res = await axios.post(`${base_url}/api/admin/mailing/send`, {
        subject,
        markdown,
        test: test || undefined,
      });
      setBatch({ total: res.data.total, sent: 0, failures: [], done: false });
      pollStatus(res.data.batch_id);
    } catch (e) {
      setError(
        e.response?.data?.error || "Could not start sending. Please try again."
      );
      setSending(false);
    }
  };

  const handleSendTest = () => {
    if (!testEmails.trim()) {
      setError("Enter one or more test addresses first.");
      return;
    }
    doSend(testEmails.trim());
  };

  const handleConfirmSend = () => {
    setConfirmOpen(false);
    doSend(null);
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex flex-col gap-4 bg-white border border-gray-200 rounded-lg p-5">
        <TextField
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label="Body (Markdown)"
          value={markdown}
          onChange={(e) => setMarkdown(e.target.value)}
          multiline
          minRows={10}
          fullWidth
        />
        <TextField
          label="Test addresses (comma separated, optional)"
          value={testEmails}
          onChange={(e) => setTestEmails(e.target.value)}
          size="small"
          fullWidth
          helperText="When set, 'Send test email' delivers only to these addresses instead of the delegates."
        />

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3 flex-wrap items-center">
          <Button variant="outlined" disabled={!ready || previewing} onClick={loadPreview}>
            {previewing ? "Loading preview…" : "Preview"}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            disabled={!ready || sending}
            onClick={handleSendTest}
          >
            Send test email
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!ready || sending}
            onClick={() => setConfirmOpen(true)}
          >
            Send to all delegates
          </Button>
        </div>
      </div>

      {batch && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col gap-2">
          <p className="text-sm font-medium text-gray-700">
            {batch.done
              ? `Finished: sent ${batch.sent} / ${batch.total} email(s).`
              : `Sending… ${batch.sent} / ${batch.total}`}
          </p>
          <LinearProgress
            variant="determinate"
            value={batch.total ? (batch.sent / batch.total) * 100 : 0}
          />
          {batch.failures?.length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-red-600 font-medium">
                {batch.failures.length} failure(s):
              </p>
              <ul className="text-xs text-red-600 list-disc list-inside">
                {batch.failures.map((f, i) => (
                  <li key={i}>
                    {f.email}: {f.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {previewHtml !== null && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col gap-3">
          <p className="text-sm font-medium text-gray-700">
            {recipientCount} recipient(s)
            {recipientsSample.length > 0 && (
              <span className="text-gray-400">
                {" "}
                — e.g. {recipientsSample.map((r) => r.email).join(", ")}
                {recipientCount > recipientsSample.length ? ", …" : ""}
              </span>
            )}
          </p>
          <iframe
            title="Email preview"
            srcDoc={previewHtml}
            className="w-full border border-gray-200 rounded"
            style={{ height: "600px" }}
          />
        </div>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Send to all delegates</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will send "<strong>{subject}</strong>" to every registered
            delegate with a completed registration
            {recipientCount !== null ? ` (${recipientCount} recipient(s))` : ""}.
            This cannot be undone. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSend} variant="contained" color="primary">
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
